import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Plus, GripVertical, Save, Trash2, BookOpen, CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { produce } from 'immer';
import { toast } from 'react-hot-toast';
import {
  useUpdateCourseCurriculumMutation,
  useAddSectionMutation,
  useAddLessonMutation,
  useDeleteSectionMutation,
} from '../../../api/apiSlice';
import LessonItem from './LessonItem';
import Loading from '../../common/Loading';
import ConfirmDialog from '../../common/ConfirmDialog';
import { formatAgo } from '../../../lib/useDraftAutosave';

const sanitizeSections = (raw) =>
  (Array.isArray(raw) ? raw : []).filter((s) => s && s._id && Array.isArray(s.lessons));

// localStorage-ключ под черновик курса. По courseId, чтобы при работе с
// несколькими курсами они не пересекались.
const DRAFT_KEY = (courseId) => `uaqyt:curriculum-draft:${courseId}`;
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 дней — больше не имеет смысла

function readDraft(courseId) {
  try {
    const raw = localStorage.getItem(DRAFT_KEY(courseId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.sections || !parsed.savedAt) return null;
    if (Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(DRAFT_KEY(courseId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeDraft(courseId, sections) {
  try {
    localStorage.setItem(
      DRAFT_KEY(courseId),
      JSON.stringify({ sections, savedAt: Date.now() })
    );
  } catch {
    // QuotaExceeded — игнорируем, autosave всё равно отработает.
  }
}

function clearDraft(courseId) {
  try {
    localStorage.removeItem(DRAFT_KEY(courseId));
  } catch {
    // ignore
  }
}

const CurriculumBuilder = ({ initialSections = [], courseId }) => {
  const { t } = useTranslation();
  const [sections, setSections] = useState(() => sanitizeSections(initialSections));
  const [pendingDeleteSectionId, setPendingDeleteSectionId] = useState(null);
  // На первом mount чистим любой залежавшийся черновик в localStorage —
  // юзер просил убрать UI «Восстановить?». Защита от случайной потери
  // теперь только через beforeunload + ручной сейв. Драфт продолжает
  // писаться в localStorage (на случай если в будущем захотим вернуть UI),
  // но не предлагается к восстановлению.
  useEffect(() => {
    if (courseId) clearDraft(courseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const lastSavedRef = useRef(JSON.stringify(sanitizeSections(initialSections)));
  // dirty — это state (для рендера UI «Не сохранено») И ref (для синхронного
  // чтения в useEffect[initialSections], где state может быть stale из-за
  // React-батчинга). Ref обновляется СИНХРОННО с setIsDirty, поэтому stale
  // closure исключён — даже если refetch прилетит в плотной последовательности
  // событий, useEffect прочитает актуальное значение через ref.
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);
  // Timestamp последнего успешного серверного сейва — для UI «сохранено N сек назад».
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // Тик каждую 30 сек чтобы «N мин назад» обновлялось.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => forceTick((x) => x + 1), 30_000);
    return () => clearInterval(i);
  }, []);

  const [updateCurriculum, { isLoading: isSaving }] = useUpdateCourseCurriculumMutation();
  const [addSection, { isLoading: isAddingSection }] = useAddSectionMutation();
  const [addLesson, { isLoading: isAddingLesson }] = useAddLessonMutation();
  const [deleteSection, { isLoading: isDeletingSection }] = useDeleteSectionMutation();

  // Sync with backend when initialSections change (after refetch).
  // КРИТИЧНО: НЕ перезаписываем локальный state, если юзер сейчас печатает
  // (dirtyRef = true) — иначе RTK Query refetch при возврате на вкладку
  // сотрёт текст, который юзер только что набрал.
  //
  // Стратегия:
  //   • Если local clean (dirty=false) → принимаем серверный snapshot целиком.
  //   • Если local dirty → мерджим только серверные video-метаданные
  //     (videoKey, originalVideoName, durationMinutes) в существующие уроки.
  //     Это нужно чтобы загруженное видео-сжатие отразилось в UI без потери
  //     набранного текста теории/квиза.
  useEffect(() => {
    const next = sanitizeSections(initialSections);
    // ВАЖНО: читаем dirty через ref, а не state. Из-за React-батчинга после
    // быстрого ввода текста + клика "Добавить" useEffect[sections] мог ещё
    // не успеть выставить isDirty=true к моменту прилёта refetch. Ref всегда
    // отражает актуальную dirty-картину.
    if (!isDirtyRef.current) {
      setSections(next);
      lastSavedRef.current = JSON.stringify(next);
      return;
    }
    // Dirty merge: вытаскиваем video-поля из next по lessonId.
    const videoByLessonId = new Map();
    next.forEach((s) =>
      (s.lessons || []).forEach((l) => {
        videoByLessonId.set(l._id, {
          videoKey: l.videoKey,
          originalVideoName: l.originalVideoName,
          durationMinutes: l.durationMinutes,
        });
      })
    );
    setSections((prev) =>
      prev.map((s) => ({
        ...s,
        lessons: (s.lessons || []).map((l) => {
          const fresh = videoByLessonId.get(l._id);
          if (!fresh) return l;
          // Если на сервере появился новый videoKey — берём его, а текст
          // (title/description/theoryContent) оставляем локальный.
          return {
            ...l,
            ...(fresh.videoKey ? { videoKey: fresh.videoKey } : {}),
            ...(fresh.originalVideoName ? { originalVideoName: fresh.originalVideoName } : {}),
            ...(fresh.durationMinutes !== undefined ? { durationMinutes: fresh.durationMinutes } : {}),
          };
        }),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSections]);

  const persist = useCallback(async (snapshot) => {
    if (!Array.isArray(snapshot) || !snapshot.length) return;
    try {
      await updateCurriculum({ courseId, sections: snapshot }).unwrap();
      lastSavedRef.current = JSON.stringify(snapshot);
      isDirtyRef.current = false;
      setIsDirty(false);
      setLastSavedAt(Date.now());
      clearDraft(courseId);
      toast.success('Сохранено');
    } catch (err) {
      toast.error(err?.data?.message || t('editor.saveError'));
    }
  }, [courseId, updateCurriculum, t]);

  // Track dirty + сразу пишем в localStorage (бэкап на случай refresh).
  // Серверного autosave НЕТ — юзер сам жмёт «Сохранить» (или Ctrl/Cmd+S).
  // isDirtyRef обновляем СИНХРОННО — это страховка для useEffect[initialSections]
  // от stale closure при плотной последовательности событий.
  useEffect(() => {
    const snapshot = JSON.stringify(sections);
    const dirty = snapshot !== lastSavedRef.current;
    isDirtyRef.current = dirty;
    setIsDirty(dirty);
    if (dirty) writeDraft(courseId, sections);
  }, [sections, courseId]);

  // beforeunload — браузер сам спросит «уйти со страницы?».
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Ctrl/Cmd+S → ручной сейв (привычка из IDE).
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (isDirty) persist(sections);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sections, persist, isDirty]);

  const handleLessonChange = (sectionId, lessonId, field, value) => {
    setSections((prev) =>
      produce(prev, (draft) => {
        const section = draft.find((s) => s._id === sectionId);
        if (!section) return;
        const lesson = section.lessons?.find((l) => l._id === lessonId);
        if (!lesson) return;
        lesson[field] = value;
      })
    );
  };

  const handleSectionChange = (sectionId, field, value) => {
    setSections((prev) =>
      produce(prev, (draft) => {
        const section = draft.find((s) => s._id === sectionId);
        if (!section) return;
        section[field] = value;
      })
    );
  };

  const handleAddSection = async () => {
    try {
      const newSection = await addSection({ courseId, title: t('editor.newSection') }).unwrap();
      if (!newSection?._id) throw new Error('bad payload');
      // ВАЖНО: НЕ обновляем lastSavedRef — это привело бы к isDirty=false и
      // потере не-сохранённого текста в других уроках при следующем refetch.
      // Раздел уже создан на сервере (POST), а локальный текст всё ещё dirty —
      // пусть юзер сохранит вручную (или через Ctrl+S).
      setSections((prev) => [...prev, { ...newSection, lessons: [] }]);
      toast.success(t('editor.sectionAdded'));
    } catch (err) {
      toast.error(err?.data?.message || t('editor.addError'));
    }
  };

  const handleAddLesson = async (sectionId) => {
    try {
      const newLesson = await addLesson({
        courseId,
        sectionId,
        title: t('editor.newLesson'),
      }).unwrap();
      if (!newLesson?._id) throw new Error('bad payload');
      setSections((prev) =>
        produce(prev, (draft) => {
          const section = draft.find((s) => s._id === sectionId);
          if (!section) return;
          if (!Array.isArray(section.lessons)) section.lessons = [];
          section.lessons.push(newLesson);
        })
      );
      toast.success(t('editor.lessonAdded'));
    } catch (err) {
      toast.error(err?.data?.message || t('editor.addError'));
    }
  };

  const handleDeleteSection = async () => {
    if (!pendingDeleteSectionId) return;
    try {
      await deleteSection({ courseId, sectionId: pendingDeleteSectionId }).unwrap();
      setSections((prev) => prev.filter((s) => s._id !== pendingDeleteSectionId));
      toast.success(t('editor.sectionDeleted'));
    } catch (err) {
      toast.error(err?.data?.message || t('editor.deleteError'));
    } finally {
      setPendingDeleteSectionId(null);
    }
  };

  const handleManualSave = () => persist(sections);

  const validSections = useMemo(
    () => sections.filter((s) => s && s._id && Array.isArray(s.lessons)),
    [sections]
  );

  const sectionToDelete = sections.find((s) => s._id === pendingDeleteSectionId);

  return (
    <div>
      {/* Sticky save-bar — всегда сверху видимо. Юзер сам решает когда
          сохранить, без сюрпризов. Цвет показывает состояние:
            • indigo + кнопка active → есть несохранённые правки
            • slate + кнопка disabled → всё сохранено */}
      <div
        className={`sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 mb-6 backdrop-blur-sm border-b transition-colors ${
          isDirty
            ? 'bg-indigo-50/80 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-900/40'
            : 'bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-800'
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white truncate">
              {t('editor.curriculumTitle')}
            </h2>
            <p className="text-xs mt-0.5 inline-flex items-center gap-1.5">
              {isSaving ? (
                <span className="inline-flex items-center gap-1.5 text-indigo-600 dark:text-indigo-400">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Сохраняем…
                </span>
              ) : isDirty ? (
                <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-400 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  Несохранённые изменения · нажмите «Сохранить»
                </span>
              ) : lastSavedAt ? (
                <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="w-3 h-3" />
                  Сохранено {formatAgo(lastSavedAt)}
                </span>
              ) : (
                <span className="text-slate-400 dark:text-slate-500">
                  Правьте свободно — сохранение по кнопке (Ctrl/⌘+S)
                </span>
              )}
            </p>
          </div>
          <button
            onClick={handleManualSave}
            disabled={isSaving || !isDirty}
            className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {isSaving ? <Loading variant="button" size="sm" /> : <Save size={16} />}
            <span className="hidden sm:inline">{t('common.save')}</span>
          </button>
        </div>
      </div>

      {validSections.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 p-10 text-center">
          <BookOpen className="mx-auto mb-3 h-10 w-10 text-slate-400" />
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('editor.emptyCurriculum')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {validSections.map((section, idx) => (
            <div
              key={section._id}
              className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-4"
            >
              <div className="flex items-center gap-2 mb-3">
                <GripVertical className="text-slate-400 shrink-0" size={18} />
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400 shrink-0">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <input
                  type="text"
                  value={section.title || ''}
                  onChange={(e) => handleSectionChange(section._id, 'title', e.target.value)}
                  placeholder={t('editor.sectionTitlePlaceholder')}
                  className="flex-1 bg-transparent font-semibold text-slate-900 dark:text-white border-b border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:border-indigo-500 focus:outline-none p-1"
                />
                <button
                  type="button"
                  onClick={() => setPendingDeleteSectionId(section._id)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/50"
                  title={t('editor.deleteSection')}
                >
                  <Trash2 size={14} />
                  <span className="hidden sm:inline">{t('editor.deleteSection')}</span>
                </button>
              </div>

              <div className="ml-2 sm:ml-6 space-y-2">
                {section.lessons
                  .filter((lesson) => lesson && lesson._id)
                  .map((lesson) => (
                    <LessonItem
                      key={lesson._id}
                      lesson={lesson}
                      courseId={courseId}
                      sectionId={section._id}
                      onLessonChange={(field, value) =>
                        handleLessonChange(section._id, lesson._id, field, value)
                      }
                    />
                  ))}

                <button
                  onClick={() => handleAddLesson(section._id)}
                  disabled={isAddingLesson}
                  className="inline-flex items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50"
                >
                  <Plus size={14} />
                  {isAddingLesson ? t('editor.adding') : t('editor.addLesson')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={handleAddSection}
        disabled={isAddingSection}
        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-emerald-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
      >
        <Plus size={16} />
        {isAddingSection ? t('editor.adding') : t('editor.addSection')}
      </button>

      <ConfirmDialog
        open={Boolean(pendingDeleteSectionId)}
        title={t('editor.deleteSectionConfirmTitle')}
        description={t('editor.deleteSectionConfirmDesc', {
          title: sectionToDelete?.title || '',
          count: sectionToDelete?.lessons?.length || 0,
        })}
        confirmLabel={t('editor.deleteSection')}
        destructive
        loading={isDeletingSection}
        onConfirm={handleDeleteSection}
        onCancel={() => setPendingDeleteSectionId(null)}
      />

      {/* Floating Save button — fixed к viewport, всегда виден при скролле.
          Появляется ТОЛЬКО когда есть несохранённые правки, чтобы не маячить
          без нужды. Sticky-bar сверху сохранён для статуса/контекста. */}
      {isDirty && (
        <button
          type="button"
          onClick={handleManualSave}
          disabled={isSaving}
          className="fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-50 inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 text-white px-5 py-3.5 text-sm font-semibold shadow-2xl shadow-indigo-500/40 hover:from-indigo-700 hover:to-purple-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          title="Сохранить (Ctrl/⌘+S)"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save size={18} />
          )}
          <span>{isSaving ? 'Сохраняем…' : t('common.save')}</span>
        </button>
      )}
    </div>
  );
};

export default CurriculumBuilder;
